import * as React from "react";
import * as Sentry from "@sentry/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useDeleteBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { useDeleteSubcollectionMembersMutation } from "@/modules/snapshots/snapshotCollection.api";
import { toast } from "react-toastify";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  subcollectionId?: string;
}

export const BusinessReportOutcomeDeleteModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  businessReportOutcome,
  subcollectionId,
}) => {
  const [deleteBusinessReportOutcome, { isLoading }] = useDeleteBusinessReportOutcomeMutation();
  const [deleteSubcollectionMembers, { isLoading: isRemovingFromSubcollection }] =
    useDeleteSubcollectionMembersMutation();
  const isSubcollectionView = !!subcollectionId;

  const handleRemove = async () => {
    try {
      if (subcollectionId) {
        await deleteSubcollectionMembers({
          id: subcollectionId,
          business_report_outcome_ids: [businessReportOutcome.id],
        }).unwrap();
      } else {
        await deleteBusinessReportOutcome({ id: businessReportOutcome.id }).unwrap();
      }
      setIsOpen(false);
      toast.success(
        isSubcollectionView
          ? `${businessReportOutcome.name || "Snapshot"} has been removed from this sub-collection.`
          : `${businessReportOutcome.name || "Snapshot"} has been removed successfully.`,
      );
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isSubcollectionView ? "Remove from Sub-collection" : "Remove Snapshot"}</DialogTitle>
          <DialogDescription>
            {isSubcollectionView
              ? `This will remove ${businessReportOutcome.name || "SnapShot"} from this sub-collection only. It will remain in the parent collection and any other sub-collections.`
              : `Are you sure you want to remove ${businessReportOutcome.name || "SnapShot"}? This action cannot be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={isLoading || isRemovingFromSubcollection}>
            {isLoading || isRemovingFromSubcollection ? "Removing..." : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
